/** @jsx jsx */

import { ComponentProps } from 'react';

import { Button } from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import AsyncButton from 'app/developer-v3/components/AsyncButton';
import LinkButton from 'app/common/components/LinkButton';

const Styles = {
  root: (margin: string) => css`
    display: flex;
    justify-content: flex-end;
    margin: ${margin};
  `,
  grid: (numButtons: number) => css`
    display: grid;
    gap: 10px 20px;
    grid-template-columns: repeat(${numButtons}, 1fr);
    justify-content: end;
  `,
};

const defaultProps = {
  cancelColor: 'secondary' as const,
  cancelText: 'Cancel',
  isDisabled: false,
  isSaveButtonVisible: true,
  margin: '10px 0px 0px 0px',
  saveColor: 'primary' as const,
  saveText: 'Save',
};

type Props = {
  cancelColor?: ComponentProps<typeof Button>['variant'];
  cancelRoute?: string;
  cancelText: string;
  isCancelDisabled?: boolean;
  isDisabled: boolean;
  isSaveButtonVisible: boolean;
  isSaving: boolean;
  margin?: string;
  onCancel?: () => void;
  onSave: () => void;
  saveColor?: ComponentProps<typeof Button>['variant'];
  saveText: string;
};

const SaveButtonGroup = ({
  cancelColor = defaultProps.cancelColor,
  cancelRoute,
  cancelText,
  isCancelDisabled: _isCancelDisabled,
  isDisabled,
  isSaveButtonVisible,
  isSaving,
  margin = defaultProps.margin,
  onCancel,
  onSave,
  saveColor = defaultProps.saveColor,
  saveText,
}: Props) => {
  const isCancelVisible = !!cancelRoute || !!onCancel;
  const numButtons = Number(!!isCancelVisible) + Number(isSaveButtonVisible);

  return (
    numButtons && (
      <div css={Styles.root(margin)}>
        <div css={Styles.grid(numButtons)}>
          {isCancelVisible && (
            <LinkButton
              aria-label={cancelText}
              href={cancelRoute}
              onPress={onCancel}
              variant={cancelColor}
            >
              {cancelText}
            </LinkButton>
          )}
          {isSaveButtonVisible && (
            <AsyncButton
              aria-label={saveText}
              isDisabled={isDisabled || isSaving}
              isWorking={isSaving}
              onPress={onSave}
              variant={saveColor}
            >
              {saveText}
            </AsyncButton>
          )}
        </div>
      </div>
    )
  );
};

SaveButtonGroup.defaultProps = defaultProps;

export default SaveButtonGroup;
